import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { backendUrl } from '../utils/variables';
import { MyContext } from '../MainContext';
import { distanceKm, reverseGeocode } from '../utils/geocoding';
import Header from '../components/header';
import Footer from '../components/footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChurch, faMapMarkerAlt, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

const FIT_ZOOM_MAX = 14;
const FIT_ZOOM_MIN = 9;
const FIT_PADDING = [40, 40];
const RADIUS_KM = 50;
const DEFAULT_LAT = 6.5244;
const DEFAULT_LNG = 3.3792;

const createUserMarkerIcon = () =>
  L.divIcon({
    className: 'custom-marker-user',
    html: `<div style="background-color:#0d6efd;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });

const createChurchMarkerIcon = () =>
  L.divIcon({
    className: 'custom-marker-church',
    html: `<div style="background-color:#198754;width:24px;height:24px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
  });

function MapViewUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] != null && center[1] != null) {
      map.setView(center, zoom);
    }
  }, [map, center, zoom]);
  return null;
}

function FitBoundsUpdater({ userLocation, churches }) {
  const map = useMap();
  useEffect(() => {
    const points = [];
    if (userLocation) {
      points.push([userLocation.latitude, userLocation.longitude]);
    }
    churches.forEach((c) => {
      const lat = Number(c.latitude);
      const lng = Number(c.longitude);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        points.push([lat, lng]);
      }
    });
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: FIT_ZOOM_MAX, minZoom: FIT_ZOOM_MIN });
  }, [map, userLocation, churches]);
  return null;
}

const FindChurch = () => {
  const { user } = useContext(MyContext);
  const navigate = useNavigate();
  const [userLocation, setUserLocation] = useState(null);
  const [locationLabel, setLocationLabel] = useState(null);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationError, setLocationError] = useState(null);

  const getLocation = () => {
    setLocationError(null);
    setLocationLabel(null);
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      setUserLocation(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        setLocationError(null);
        try {
          const name = await reverseGeocode(latitude, longitude);
          if (name) setLocationLabel(name);
        } catch (_) {}
      },
      (err) => {
        const msg = err.message || 'Could not get location';
        const friendly = msg.toLowerCase().includes('timeout')
          ? 'Location timed out. Try again or move to an area with better signal.'
          : msg;
        setLocationError(friendly);
        setUserLocation(null);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    getLocation();
  }, []);

  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    const lat = userLocation?.latitude;
    const lng = userLocation?.longitude;

    if (lat == null || lng == null) {
      setChurches([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        let list = [];
        const res = await fetch(`${backendUrl}/get_churches_with_location.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.user_id,
            latitude: lat,
            longitude: lng,
            radius_km: RADIUS_KM,
          }),
        });
        const data = await res.json();
        if (data?.success && Array.isArray(data.data)) list = data.data;

        if (list.length === 0) {
          const fallback = await fetch(`${backendUrl}/get_user_groups.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: user.user_id, status: 'active' }),
          });
          const fallbackData = await fallback.json();
          if (fallbackData?.success && Array.isArray(fallbackData.data)) {
            list = fallbackData.data.filter(
              (g) => g.group_type === 'church' && g.latitude != null && g.longitude != null
            );
          }
        }

        if (list.length > 0) {
          list = list
            .map((c) => ({
              ...c,
              _distance: c.distance_km != null ? Number(c.distance_km) : distanceKm(lat, lng, Number(c.latitude), Number(c.longitude)),
            }))
            .filter((c) => c._distance <= RADIUS_KM)
            .sort((a, b) => (a._distance || 0) - (b._distance || 0));
        }

        if (!cancelled) setChurches(list);
      } catch (e) {
        if (!cancelled) {
          try {
            const fallback = await fetch(`${backendUrl}/get_user_groups.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: user.user_id, status: 'active' }),
            });
            const fallbackData = await fallback.json();
            if (fallbackData?.success && Array.isArray(fallbackData.data)) {
              let list = fallbackData.data.filter(
                (g) => g.group_type === 'church' && g.latitude != null && g.longitude != null
              );
              list = list
                .map((c) => ({
                  ...c,
                  _distance: distanceKm(lat, lng, Number(c.latitude), Number(c.longitude)),
                }))
                .filter((c) => c._distance <= RADIUS_KM)
                .sort((a, b) => (a._distance || 0) - (b._distance || 0));
              setChurches(list);
            } else setChurches([]);
          } catch (_) {
            setChurches([]);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, userLocation?.latitude, userLocation?.longitude]);

  const openChurch = (church) => {
    const id = church.group_id ?? church.id;
    if (id) navigate(`/group/${id}`);
  };

  const mapCenterLat = userLocation?.latitude ?? DEFAULT_LAT;
  const mapCenterLng = userLocation?.longitude ?? DEFAULT_LNG;
  const mapCenter = [mapCenterLat, mapCenterLng];
  const mapZoom = 10;

  return (
    <>
      <Header />
      <div className="container-fluid p-3" style={{ marginTop: '80px', marginBottom: '80px' }}>
        <h2 className="mb-2">
          <FontAwesomeIcon icon={faChurch} className="me-2" />
          Find a church near you
        </h2>
        <p className="text-muted mb-2">Churches on Clowza with a set location appear on the map.</p>
        <p className="small text-muted mb-3">
          We use your device or browser location. On phones this is usually GPS (e.g. your actual area). On some networks or desktop it can be approximate (e.g. ISP or network location).
        </p>

        {userLocation && (
          <div className="small text-muted mb-2 d-flex flex-wrap align-items-center gap-2">
            <span>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
              Using location: {userLocation.latitude.toFixed(4)}, {userLocation.longitude.toFixed(4)}
              {locationLabel && (
                <span className="ms-1">— {locationLabel}</span>
              )}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={getLocation}>
              Refresh location
            </button>
          </div>
        )}

        {locationError && (
          <div className="alert alert-warning d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faExclamationCircle} />
            <span className="flex-grow-1">{locationError}</span>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={getLocation}>
              Retry
            </button>
          </div>
        )}

        <div className="rounded overflow-hidden border mb-4" style={{ height: 300, minHeight: 300 }}>
          {!userLocation ? (
            <div className="d-flex align-items-center justify-content-center bg-light border rounded" style={{ height: 300 }}>
              <span className="text-muted">Getting your location to center the map…</span>
            </div>
          ) : (
          <MapContainer
            key={`${userLocation.latitude}-${userLocation.longitude}`}
            center={[userLocation.latitude, userLocation.longitude]}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapViewUpdater center={mapCenter} zoom={mapZoom} />
            <FitBoundsUpdater userLocation={userLocation} churches={churches} />
            {userLocation && (
              <Marker
                position={[userLocation.latitude, userLocation.longitude]}
                icon={createUserMarkerIcon()}
              >
                <Popup>You are here</Popup>
              </Marker>
            )}
            {churches.map((church) => {
              const lat = Number(church.latitude);
              const lng = Number(church.longitude);
              if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
              const name = church.group_name || church.name || 'Church';
              const id = church.group_id ?? church.id;
              return (
                <Marker
                  key={id}
                  position={[lat, lng]}
                  icon={createChurchMarkerIcon()}
                  eventHandlers={{
                    click: () => openChurch(church),
                  }}
                >
                  <Popup>
                    <div className="text-start">
                      <strong>{name}</strong>
                      {church.address && <div className="small text-muted">{church.address}</div>}
                      {church._distance != null && (
                        <div className="small text-primary">{church._distance.toFixed(1)} km away</div>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-primary mt-2"
                        onClick={() => openChurch(church)}
                      >
                        View church
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
          )}
        </div>
        {userLocation && (
          <p className="small text-muted mb-3">
            <span className="text-primary">●</span> You are here &nbsp; <span className="text-success">●</span> Churches
          </p>
        )}

        {loading && (
          <div className="d-flex align-items-center justify-content-center py-4 gap-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <span>Loading churches…</span>
          </div>
        )}

        {!loading && churches.length === 0 && (
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faChurch} size="3x" className="text-muted mb-3" />
            <h5 className="text-muted">No churches with location yet</h5>
            <p className="text-muted small">Church groups can add their address when creating or editing the group.</p>
            {userLocation && locationLabel && (
              <p className="text-muted small mt-2">Search is based on your current location ({locationLabel}).</p>
            )}
          </div>
        )}

        {!loading && churches.length > 0 && (
          <div>
            <h5 className="mb-3">Nearby churches</h5>
            <div className="list-group">
              {churches.map((church) => {
                const id = church.group_id ?? church.id;
                return (
                  <button
                    key={id}
                    type="button"
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                    onClick={() => openChurch(church)}
                  >
                    {church.group_photo ? (
                      <img
                        src={church.group_photo}
                        alt=""
                        className="rounded-circle"
                        style={{ width: 48, height: 48, objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center bg-light"
                        style={{ width: 48, height: 48 }}
                      >
                        <FontAwesomeIcon icon={faChurch} className="text-primary" />
                      </div>
                    )}
                    <div className="flex-grow-1 text-start">
                      <div className="fw-semibold">{church.group_name || church.name}</div>
                      {church.address && <div className="small text-muted">{church.address}</div>}
                      {church._distance != null && (
                        <div className="small text-primary">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                          {church._distance.toFixed(1)} km away
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <Footer page="find-church" />
    </>
  );
};

export default FindChurch;
